import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';

/**
 * Base service for making API calls to the Enrich API
 * This service handles the common functionality for all Enrich API calls
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
    private http = inject(HttpClient);

  private apiUrl = this.buildUrl(environment.enrichBaseServerUrl, 'api');

  /**
   * Makes a GET request to the specified endpoint
   * @param endpoint The API endpoint to call
   * @param params Optional query parameters
   * @returns An Observable of the response
   */
  get<T>(endpoint: string, params?: any): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, { params });
  }

  /**
   * Makes a POST request to the specified endpoint
   * @param endpoint The API endpoint to call
   * @param data The data to send in the request body
   * @param params Optional query parameters
   * @returns An Observable of the response
   */
  post<T>(endpoint: string, data: any, params?: any): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, data, { params });
  }

  /**
   * Makes a PUT request to the specified endpoint
   * @param endpoint The API endpoint to call
   * @param data The data to send in the request body
   * @param params Optional query parameters
   * @returns An Observable of the response
   */
  put<T>(endpoint: string, data: any, params?: any): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data, { params });
  }

  /**
   * Makes a DELETE request to the specified endpoint
   * @param endpoint The API endpoint to call
   * @param params Optional query parameters
   * @returns An Observable of the response
   */
  delete<T>(endpoint: string, params?: any): Observable<T> {
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, { params });
  }

  /**
   * Builds a URL by joining base and path, ensuring there are no double slashes
   * @param base The base URL (may or may not end with a slash)
   * @param path The path to append (may or may not start with a slash)
   * @returns A properly formatted URL
   */
  private buildUrl(base: string, path: string): string {
    // Remove trailing slash from base if present
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    // Remove leading slash from path if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    // Join with a single slash
    return `${cleanBase}/${cleanPath}`;
  }

  /**
   * Gets the full API URL for the specified endpoint
   * @param endpoint The API endpoint
   * @returns The full API URL
   */
  getFullUrl(endpoint: string): string {
    return `${this.apiUrl}/${endpoint}`;
  }
}
